import { useState, useCallback } from "react";

interface FetchOptions<T> {
  body?: object;
  doOnSuccess?: (data: T) => void;
  doOnError?: (error: String) => void;
}

const useFetch = <T,>(
  token: string | null = null,
  setToken: ((token: string) => void) | null = null
) => {
  const [data, setData] = useState<T | null>(null);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Use environment variable from .env-file
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const fetchData = useCallback(
    <T,>(
      endOfUrl: string,
      method: string,
      options: FetchOptions<T> = {}
    ): Promise<T> => {
      const {
        body = null,
        doOnSuccess = () => {},
        doOnError = () => {},
      } = options;
      setIsPending(true);
      setError(null);

      // Only set JSON Content-Type if body is NOT FormData, otherwise not setting the content-type to enable sending a multipart-form
      const isFormData = body instanceof FormData;

      const headers: HeadersInit = {};
      if (!isFormData) headers["Content-Type"] = "application/json";

      return new Promise<T>((resolve, reject) => {
        fetch(`${apiBaseUrl}` + endOfUrl, {
          method: method,
          credentials: "include", // include cookies (JSESSIONID for authentication)
          headers: method === "GET" || isFormData ? undefined : headers,
          body:
            method === "GET" ? null : isFormData ? body : JSON.stringify(body), // No body for GET
        })
          .then((res) => {
            if (!res.ok) {
              return res.text().then((text) => {
                throw Error(text || res.statusText);
              });
            }
            return res.text().then((text) => (text ? JSON.parse(text) : {}));
          })
          .then((data) => {
            setData(data);
            setIsPending(false);
            doOnSuccess(data);
            resolve(data);
          })
          .catch((err) => {
            setError(err.message);
            doOnError(err.message);
            setIsPending(false);
            reject(err);
          });
      });
    },
    [token]
  );

  return { data, isPending, error, fetchData };
};

export default useFetch;
